/**
 * web/src/screens/Life/Chores/index.tsx — the Household screen the router
 * mounts (tab key 'chores' is unchanged; the label is Household). The former
 * 1,832-line single-file screen split into section modules (card
 * jJbBjhgw0fk0): this file is orchestration only — it calls useChores() and
 * composes the sections, passing state down as props.
 *
 * The week table moved to its own Life sub-tab (Week), so this screen is the
 * working list: composer, common picks, lifecycle cards, the rows, the
 * Google card — plus the tasks the weekly sync filed, shown with their
 * source chip rather than in a separate Plan-only table.
 */
import { useEffect, useState } from 'react';
import { api } from '../../../shell/bridge';
import { KudosToast } from '../../Requests';
import { useChores } from './hooks';
import { toastLabel } from './helpers';
import { ChoreComposer } from './composer';
import { SeedLibrary } from './seeds';
import { StreakNotice, CapCard } from './lifecycle';
import { ChoreList } from './list';
import { GoogleCard } from './google';
import '../chores.css';

/** One task the weekly sync filed (GET /api/plan/tasks). Read-only here:
 * there is no per-task transition endpoint, so the row links to Plan, where
 * the meeting it came from lives. */
interface SyncTask {
  taskId: string;
  title: string;
  assignee: 'me' | 'partner' | 'anyone';
  state: 'open' | 'done';
  createdBy: string;
}

/**
 * Household panel — the shared list of things to do around the home. One row
 * per item: cadence chip (1x vs ↺ recurring), an assignee pill that cycles
 * me → partner → anyone, the date it is due (or next due, for the recurring
 * ones), and a tick-off that celebrates with the same toast the kudos card
 * uses.
 */
export function Chores() {
  const {
    me, items, failed, otherMember, partnerName, partnerPhrase,
    open, done, celebratedChore, cappedChore,
    title, setTitle, cadence, setCadence, freq, setFreq, due, setDue, time, setTime,
    assignee, setAssignee, assignMode, setAssignMode, assignModePicked, setAssignModePicked,
    recurMode, setRecurMode, every, setEvery, editing, addToGoogle, setAddToGoogle,
    adding, everyDays, timeWithoutDate, submitChore, resetComposer, beginEdit,
    picked, setPicked, toggleSeed, addSeeds,
    busyId, armedDelete, removeChore, tapAssignee, complete,
    reactBusyId, react, noteFor, setNoteFor, noteDraft, setNoteDraft, saveNote, openNote,
    laterFor, setLaterFor, capped, setCapped, laterNote, setLaterNote, streakLabel, setStreakLabel,
    pushLater, renegotiate, celebrated,
    google, gBusy, gNote, connectGoogle, disconnectGoogle,
  } = useChores();

  const [syncTasks, setSyncTasks] = useState<SyncTask[] | null>(null);
  useEffect(() => {
    let live = true;
    api<{ tasks: SyncTask[] }>('/api/plan/tasks')
      .then(t => { if (live) setSyncTasks(t.tasks ?? []); })
      .catch(() => { if (live) setSyncTasks([]); });
    const reload = () => {
      api<{ tasks: SyncTask[] }>('/api/plan/tasks')
        .then(t => { if (live) setSyncTasks(t.tasks ?? []); })
        .catch(() => {});
    };
    window.addEventListener('pear:plan', reload);
    window.addEventListener('focus', reload);
    return () => { live = false; window.removeEventListener('pear:plan', reload); window.removeEventListener('focus', reload); };
  }, []);

  const openSyncTasks = (syncTasks ?? []).filter(t => t.state === 'open');
  const syncWho = (t: SyncTask): string => {
    if (t.assignee === 'anyone') return 'Anyone';
    return t.createdBy === me?.memberId
      ? t.assignee === 'me' ? 'You' : 'Partner'
      : t.assignee === 'me' ? 'Partner' : 'You';
  };

  return (
    <section className="ch-panel" id="life-chores-panel" aria-labelledby="ch-title">
      <h2
        id="ch-title"
        className="ch-title"
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'var(--type-h3-size)',
          letterSpacing: 'var(--type-tracking-wider)',
          fontWeight: 400,
          color: 'var(--color-cream)',
        }}
      >
        Household
      </h2>

      <ChoreComposer
        editing={editing} title={title} setTitle={setTitle}
        cadence={cadence} setCadence={setCadence} freq={freq} setFreq={setFreq}
        due={due} setDue={setDue} time={time} setTime={setTime}
        assignee={assignee} setAssignee={setAssignee}
        assignMode={assignMode} setAssignMode={setAssignMode}
        assignModePicked={assignModePicked} setAssignModePicked={setAssignModePicked}
        recurMode={recurMode} setRecurMode={setRecurMode} every={every} setEvery={setEvery}
        adding={adding} everyDays={everyDays} timeWithoutDate={timeWithoutDate}
        google={google} addToGoogle={addToGoogle} setAddToGoogle={setAddToGoogle}
        submitChore={submitChore} resetComposer={resetComposer}
        otherMember={otherMember} partnerName={partnerName}
      />

      {/* Seed library (card VmoORelj1HuO): the common picks, grouped,
          multi-pick — tap several, add them together, and the composer
          above stays what it was for the one you are actually typing. */}
      <SeedLibrary picked={picked} setPicked={setPicked} toggleSeed={toggleSeed} addSeeds={addSeeds} adding={adding} />

      {/* Lifecycle (card xvom08w4BgGT): the streak celebration, the cap's
          options card, and the chooser's feedback — all about the LIST, so
          they sit above it rather than inside any one row. */}
      {streakLabel && <StreakNotice streakLabel={streakLabel} setStreakLabel={setStreakLabel} />}

      {cappedChore && (
        <CapCard cappedChore={cappedChore} busyId={busyId} setCapped={setCapped} tapAssignee={tapAssignee} renegotiate={renegotiate} />
      )}

      {laterNote && <p className="rq-empty-hint ch-later-note" id="ch-later-note" role="status">{laterNote}</p>}

      {/* Tasks the weekly sync filed — same list, source chip on the row, so
          one list holds everything the couple owes (nothing here is
          completable from this screen; Plan owns the meeting they came from). */}
      {openSyncTasks.length > 0 && (
        <section className="ch-sync" data-test="ch-sync-tasks" aria-label="From the weekly sync">
          <h3 className="plan-section-title ch-sync-title">
            From the weekly sync ({openSyncTasks.length})
          </h3>
          <ul className="ch-sync-list">
            {openSyncTasks.map(t => (
              <li className="td-row ch-sync-row" key={t.taskId} data-test="ch-sync-task">
                <span className="life-kind-chip life-kind-sync">Weekly sync</span>
                <span className="td-text">{t.title}</span>
                <span className="td-meta">{syncWho(t)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <ChoreList
        items={items} failed={failed} open={open} done={done} me={me} partnerPhrase={partnerPhrase}
        busyId={busyId} armedDelete={armedDelete} laterFor={laterFor} setLaterFor={setLaterFor} setLaterNote={setLaterNote}
        noteFor={noteFor} setNoteFor={setNoteFor} noteDraft={noteDraft} setNoteDraft={setNoteDraft}
        reactBusyId={reactBusyId} react={react} saveNote={saveNote} openNote={openNote}
        tapAssignee={tapAssignee} complete={complete} beginEdit={beginEdit} removeChore={removeChore} pushLater={pushLater}
      />

      <GoogleCard google={google} gBusy={gBusy} gNote={gNote} connectGoogle={connectGoogle} disconnectGoogle={disconnectGoogle} />

      {/* Same celebration primitive the kudos card ships (HBYCnnD9q5FS) —
          reused, not re-drawn, so both surfaces celebrate identically. The
          kudos glyph stays with kudos: a tick-off reads as its own
          line, not a sparkle (founder report 2026-10-08). */}
      {celebrated && (
        <KudosToast
          glyph=""
          variant="shimmer"
          label={toastLabel(celebratedChore, me)}
        />
      )}
    </section>
  );
}
